import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Library } from '@/components/workspace/tailoring/Library';
import { PrivacyNotice } from '@/components/common/PrivacyNotice';
import { JobDescriptionInput } from '@/components/workspace/tailoring/JobInput';
import { TargetDetailsForm } from '@/components/workspace/tailoring/TargetForm';
import { Briefcase, FileSignature, Users, Send, FileJson, PiggyBank, LogIn, Search, ArrowLeft } from 'lucide-react';
import { useWorkspaceContext } from '@/components/workspace/WorkspaceContext';
import { useAuth } from '@/contexts/AuthContext';
import { LoginModal } from '@/components/auth/LoginModal';
import toast from 'react-hot-toast';

export const Sidebar: React.FC<{ onBackToOptions?: () => void }> = ({ onBackToOptions }) => {
  const {
    resumes, selectedId, selectResume, deleteResume, uploadResume,
    jobText, setJobText, jobLink, setJobLink,
    isFetchingLink, setIsFetchingLink, isExtracting, extractMetadataForText,
    setStatus, setStatusMessage, targetDetails, setTargetDetails,
    hasInputs, isWorking, isGenerating,
    handleAnalyze, handleGenerateCoverLetter, handleGenerateTailoredResume,
    handleGenerateInterviewPrep, handleGenerateCommunications
  } = useWorkspaceContext();

  const { user } = useAuth();
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    const handleRequireLogin = () => {
      setShowLogin(true);
    };
    window.addEventListener('app:require-login', handleRequireLogin);
    return () => {
      window.removeEventListener('app:require-login', handleRequireLogin);
    };
  }, []);

  const requireAuth = (action: () => void) => {
    if (!user) {
      setShowLogin(true);
      return;
    }
    if (!user.emailVerified) {
      toast.error('Please verify your email address to continue.');
      return;
    }
    action();
  };

  return (
    <aside className="w-full lg:w-[420px] shrink-0 border-b lg:border-b-0 lg:border-r border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 flex flex-col z-10 lg:h-full min-h-0 relative">
      {showLogin && <LoginModal isOpen={true} onClose={() => setShowLogin(false)} />}
      <div className="flex-1 lg:overflow-y-auto p-4 lg:p-6 space-y-6 lg:space-y-8 scrollbar-thin scrollbar-thumb-white/10">
        <motion.div initial={{opacity: 0, y: 10}} animate={{opacity: 1, y: 0}} transition={{duration: 0.3}}>
          {onBackToOptions && (
            <button
               onClick={onBackToOptions}
               className="mb-8 flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 cursor-pointer"
             >
               <ArrowLeft className="w-4 h-4" /> Back to Options
            </button>
          )}
          <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-400 tracking-wider uppercase mb-3 px-1">1. Select Resume</h2>
          <Library 
            resumes={resumes} selectedId={selectedId} onSelect={selectResume} onDelete={deleteResume} onUpload={uploadResume}
          />
          <PrivacyNotice />
        </motion.div>

        <div>
          <JobDescriptionInput
            jobText={jobText} setJobText={setJobText}
            jobLink={jobLink} setJobLink={setJobLink}
            setTargetDetails={setTargetDetails}
            extractMetadataForText={extractMetadataForText}
            isExtracting={isExtracting}
            setStatus={setStatus} setStatusMessage={setStatusMessage}
            isFetchingLink={isFetchingLink} setIsFetchingLink={setIsFetchingLink}
            setSessionCost={() => {}} setSessionTokens={() => {}}
            onRequireAuth={() => {
              if (!user) {
                setShowLogin(true);
                return false;
              }
              if (!user.emailVerified) {
                toast.error('Please verify your email address to continue.');
                return false;
              }
              return true;
            }}
          />
        </div>

        <motion.section initial={{opacity: 0, y: 10}} animate={{opacity: 1, y: 0}} transition={{duration: 0.3}} className="pb-8 mt-8">
          <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-400 tracking-wider uppercase mb-3 px-1">3. Target Details <span className="lowercase font-normal text-slate-500 dark:text-slate-400">(optional)</span></h2>
          <TargetDetailsForm details={targetDetails} onChange={setTargetDetails} isExtracting={isFetchingLink ? 'fetching' : isExtracting} />
        </motion.section>
      </div>

      <div className="p-3 lg:p-4 border-t border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 shrink-0 grid grid-cols-2 gap-2 relative">
         <button
            onClick={() => requireAuth(handleAnalyze)} disabled={!hasInputs || isWorking || isGenerating?.analysis}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 lg:py-2 bg-emerald-600 hover:bg-emerald-500 text-white border border-emerald-500/50 text-sm font-medium rounded-lg disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed col-span-2"
         >
            <Briefcase className="w-4 h-4 hidden sm:block" /> Analyze
         </button>
         <div className="relative group w-full">
            <button
               onClick={() => requireAuth(handleGenerateTailoredResume)} disabled={!hasInputs || isWorking || isGenerating?.tailoredResume}
               className="w-full flex items-center justify-center gap-2 px-3 py-2.5 lg:py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 text-xs sm:text-sm font-medium rounded-lg disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
            >
               <FileJson className="w-3.5 h-3.5 sm:w-4 sm:h-4 hidden sm:block xl:block" /> Tailored
            </button>
            <div className="absolute hidden group-hover:block transition-opacity bottom-full mb-2 left-1/2 -translate-x-1/2 w-48 bg-slate-800 dark:bg-slate-700 text-white text-[10px] p-2 rounded pointer-events-none text-center z-50">
               For better quality and results, first use Analyze
               <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800 dark:border-t-slate-700"></div>
            </div>
         </div>
         <button
            onClick={() => requireAuth(handleGenerateCoverLetter)} disabled={!hasInputs || isWorking || isGenerating?.coverLetter}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 lg:py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 text-xs sm:text-sm font-medium rounded-lg disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
         >
            <FileSignature className="w-3.5 h-3.5 sm:w-4 sm:h-4 hidden sm:block xl:block" /> Cover Letter
         </button>
         <button
            onClick={() => requireAuth(handleGenerateInterviewPrep)} disabled={!hasInputs || isWorking || isGenerating?.interviewPrep}
            className="w-full flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-2.5 lg:py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 text-xs sm:text-sm font-medium rounded-lg disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
         >
            <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 hidden sm:block xl:block" />
            <span>Interview</span>
            <span className="text-[9px] font-bold uppercase bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400 px-1 py-0.5 rounded leading-none hidden xl:inline-block">Beta</span>
         </button>
         <button
            onClick={() => requireAuth(handleGenerateCommunications)} disabled={!hasInputs || isWorking || isGenerating?.communications}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 lg:py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 text-xs sm:text-sm font-medium rounded-lg disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
         >
            <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 hidden sm:block xl:block" /> Outreach
         </button>
      </div>
    </aside>
  );
};
